import { TOverviewView, TRoutineItem, TTaskV4 } from './types'

export const isTaskOverviewView = (view: TOverviewView) => view.type !== 'routine'

export const getActiveMeetingPreparationTasks = (tasks: TTaskV4[] | undefined) =>
    tasks?.filter((task) => !task.is_done && !task.is_deleted) ?? []

export const isOverviewViewNonEmpty = (view: TOverviewView, activeMeetingPreparationCount = 0) => {
    if (!view.is_linked) return false
    if (view.type === 'routine') return view.status === 'current' && (view.remaining_count ?? 0) > 0
    if (view.type === 'meeting_preparation') return activeMeetingPreparationCount > 0
    return view.view_items.length > 0
}

export const findFirstNonEmptyOverviewView = (views: TOverviewView[], activeMeetingPreparationCount = 0) =>
    views.find((view) => isOverviewViewNonEmpty(view, activeMeetingPreparationCount))

export const getFirstTaskInOverviewView = (view: TOverviewView, activeMeetingPreparationTasks: TTaskV4[] = []) => {
    if (!isTaskOverviewView(view)) return undefined
    if (view.type === 'meeting_preparation') return activeMeetingPreparationTasks[0]
    return view.view_items[0] as TTaskV4 | undefined
}

export const didOverviewViewBecomeEmptyAfterCompletion = (
    previousView: TOverviewView,
    view: TOverviewView,
    previousActiveMeetingPreparationCount = 0,
    activeMeetingPreparationCount = 0
) => {
    if (
        previousView.id !== view.id ||
        !isOverviewViewNonEmpty(previousView, previousActiveMeetingPreparationCount) ||
        isOverviewViewNonEmpty(view, activeMeetingPreparationCount)
    )
        return false

    if (view.type === 'meeting_preparation')
        return previousActiveMeetingPreparationCount > 0 && activeMeetingPreparationCount === 0

    if (view.type === 'routine') {
        const completedItems = new Map(
            (view.view_items as TRoutineItem[]).map((item) => [item.id, item.is_completed])
        )
        return (previousView.view_items as TRoutineItem[]).some(
            (item) => !item.is_completed && completedItems.get(item.id) === true
        )
    }

    const currentIds = new Set(view.view_item_ids)
    return previousView.view_items.some((item) => !currentIds.has(item.id))
}

export const getOverviewOpenIdsAfterViewEmpties = (
    views: TOverviewView[],
    emptiedViewId: string,
    openViewIds: string[],
    activeMeetingPreparationCount = 0
) => {
    if (!openViewIds.includes(emptiedViewId)) return openViewIds
    const remainingOpenIds = openViewIds.filter((id) => id !== emptiedViewId)
    if (remainingOpenIds.length > 0) return remainingOpenIds
    const nextView = findFirstNonEmptyOverviewView(views, activeMeetingPreparationCount)
    return nextView ? [nextView.id] : []
}

// Cards move relative to a neighbouring card, never to a numeric slot: the overview list leaves out
// some saved cards (routine cards while routines are off), so a slot counted here would be off on
// the server. OverviewViewModify places the card against every saved card instead.
export type TViewMoveAnchor = { beforeViewId: string } | { afterViewId: string }

export const anchorViewId = (anchor: TViewMoveAnchor) =>
    'beforeViewId' in anchor ? anchor.beforeViewId : anchor.afterViewId

export const moveViewToAnchor = <T extends { id: string }>(
    views: readonly T[],
    id: string,
    anchor: TViewMoveAnchor
): T[] => {
    const moved = views.find((view) => view.id === id)
    const rest = views.filter((view) => view.id !== id)
    const at = rest.findIndex((view) => view.id === anchorViewId(anchor))
    if (!moved || at === -1) return [...views]
    rest.splice('beforeViewId' in anchor ? at : at + 1, 0, moved)
    return rest
}

// Anchors that move the card at `index` of the visible cards to the top, up one, or down one.
export const getCardMoveAnchors = (visibleIds: readonly string[], index: number) => {
    const isFirst = index === 0
    const isLast = index === visibleIds.length - 1
    return {
        toTop: isFirst ? undefined : { beforeViewId: visibleIds[0] },
        up: isFirst ? undefined : { beforeViewId: visibleIds[index - 1] },
        down: isLast ? undefined : { afterViewId: visibleIds[index + 1] },
    }
}

// ReorderDropContainer reports a 1-based drop slot among the visible cards.
export const getDropAnchor = (visibleIds: readonly string[], slot: number): TViewMoveAnchor | undefined => {
    if (visibleIds.length === 0) return undefined
    return slot - 1 < visibleIds.length
        ? { beforeViewId: visibleIds[Math.max(slot - 1, 0)] }
        : { afterViewId: visibleIds[visibleIds.length - 1] }
}

export const findTaskOverviewViewContainingItem = (views: TOverviewView[], itemId: string) =>
    views.find((view) => isTaskOverviewView(view) && view.view_item_ids.includes(itemId))

export const areAllTaskOverviewViewsEmpty = (views: TOverviewView[]) =>
    views.every((view) => !isTaskOverviewView(view) || view.view_items.length === 0)

export const removeOverviewView = (views: TOverviewView[], viewId: string) => views.filter((view) => view.id !== viewId)

// A newly added card goes first; every other card keeps its relative order.
export const placeOverviewViewFirst = (views: TOverviewView[], view: TOverviewView) => [
    view,
    ...removeOverviewView(views, view.id),
]

// Mirrors ensureRoutineOverviewView: a routine card goes right after the last routine card (first
// when there is none), so a new routine never takes Current from one already in progress.
export const placeRoutineViewAfterRoutines = (views: TOverviewView[], view: TOverviewView) => {
    const rest = removeOverviewView(views, view.id)
    const insertAt = rest.map((candidate) => candidate.type).lastIndexOf('routine') + 1
    return [...rest.slice(0, insertAt), view, ...rest.slice(insertAt)]
}

// Puts the views listed in `ids` in that order, keeping their current contents. Views missing from
// `ids` (a card added meanwhile) stay in the slot they are in.
export const arrangeViewsByIds = <T extends { id: string }>(views: readonly T[], ids: readonly string[]): T[] => {
    const byId = new Map(views.map((view) => [view.id, view]))
    const ordered = ids.flatMap((id) => {
        const view = byId.get(id)
        return view ? [view] : []
    })
    const known = new Set(ordered.map((view) => view.id))
    let next = 0
    return views.map((view) => (known.has(view.id) ? ordered[next++] : view))
}

/** Resolve cached task IDs without losing the order owned by the overview response. */
export const getOverviewTasksInOrder = (viewItemIds: string[], tasks: TTaskV4[] | undefined) => {
    const tasksById = new Map(tasks?.map((task) => [task.id, task]) ?? [])
    const seen = new Set<string>()
    return viewItemIds.flatMap((id) => {
        if (seen.has(id)) return []
        seen.add(id)
        const task = tasksById.get(id)
        return task ? [task] : []
    })
}
